// AADHYAA — CANONICAL FRONTEND PRICING & TIER METADATA
// This file is the SINGLE SOURCE OF TRUTH for ALL frontend pricing + tier UI metadata.
// MUST match backend pricing in /app/backend/services/pricing_config.py
//
// Last sync: Feb 25, 2026 (Phase 17)
//   - KULA repriced ₹3,499 → ₹1,999 (PM audit Move 2)
//   - Annual discount 16.6% → 20% (PM audit Rec #3)
//   - Deep Simulation ₹299 → ₹149 (PM audit Rec #11)
//   - MARGA gets 7-day free trial, no card (PM audit Rec #4)
//
// CONSUMERS (must import from here, no local duplicates):
//   - /app/frontend/src/pages/PricingPage.js
//   - /app/frontend/src/pages/LandingPage.js
//   - /app/frontend/src/components/landing/PricingSection.js
//   - /app/frontend/src/services/tierService.js (re-exports TIER_PRICING)

// ============================================================================
// TIER PRICING — raw numeric values (mirrors backend pricing_config.py)
// ============================================================================

export const TIER_PRICING = {
  prarambh: {
    id: "prarambh",
    name: "PRARAMBH",
    nameHindi: "प्रारम्भ",
    meaning: "The Beginning",
    monthlyPriceINR: 0,
    monthlyPriceUSD: 0,
    yearlyPriceINR: 0,
    yearlyPriceUSD: 0,
    isFree: true,
    askAiWeeklyLimit: 3,                  // Phase 18 gating sync — enforced in /api/ask
    familyMembers: 1,
    tierOrder: 1,
  },
  marga: {
    id: "marga",
    name: "MARGA",
    nameHindi: "मार्ग",
    meaning: "The Path",
    monthlyPriceINR: 899,
    monthlyPriceUSD: 14.99,
    yearlyPriceINR: 8630,           // 20% off (PM audit Rec #3)
    yearlyPriceUSD: 143.90,
    isFree: false,
    isPopular: true,
    annualDiscountPercent: 20,
    freeTrialDays: 7,                // PM audit Rec #4
    askAiWeeklyLimit: null,         // Unlimited
    familyMembers: 3,
    tierOrder: 2,
  },
  kula: {
    id: "kula",
    name: "KULA",
    nameHindi: "कुल",
    meaning: "The Family",
    monthlyPriceINR: 1999,           // REPRICED Feb 25, 2026 (PM audit Move 2)
    monthlyPriceUSD: 24.99,
    yearlyPriceINR: 19190,           // 20% off
    yearlyPriceUSD: 239.90,
    isFree: false,
    freeReports: true,
    askAiWeeklyLimit: null,         // Unlimited
    familyMembers: 6,
    annualDiscountPercent: 20,
    tierOrder: 3,
  },
};

// ============================================================================
// TIER VISUAL METADATA — taglines, copy, feature lists, icons
// (Icons referenced by name; consumer maps to actual component)
// ============================================================================

export const TIER_META = {
  prarambh: {
    id: "prarambh",
    nameEnglish: "The Beginning",
    tagline: "Your first step into Vedic wisdom",
    description: "Trust builder — Experience AADHYAA before committing",
    whoItsFor: "Curious seekers beginning their cosmic exploration",
    depthLevel: "See WHAT",
    emoji: "⭐",
    iconName: "Star",                    // lucide-react / phosphor name
    color: "var(--palladian)",
    bgGradient: "from-[var(--palladian-light)] to-[var(--bg-surface)]",
    borderColor: "border-[var(--palladian)]",
    textColor: "text-[var(--text-primary)]",
    period: "forever",
    cta: "Start Free",
    ctaStyle: "outline",
    unlocks: [
      "All 7 sections visible",
      "Daily Cosmic Snapshot (brief)",
      "Birth Chart Overview",
      "Basic Timing Windows",
      "3 Deep AI Questions / Week",
      "Report Previews",
      "Kids Gurukul (text stories)",
    ],
    features: [
      { name: "All 7 Sections Visible", included: true },
      { name: "Daily Cosmic Snapshot (brief)", included: true },
      { name: "Basic Birth Chart Overview", included: true },
      { name: "3 Deep AI Questions / Week", included: true },
      { name: "Report Previews", included: true },
      { name: "Kids Gurukul (text stories)", included: true },
    ],
    excludedHighlights: [
      "Full Daily Horoscope",
      "Detailed Dasha Analysis",
      "Confidence Scores",
      "Personalized Remedies",
      "PDF Downloads",
      "Audio Stories",
    ],
  },
  marga: {
    id: "marga",
    nameEnglish: "The Path",
    tagline: "Understand your life with deeper intelligence",
    description: "Complete personal guidance — Wake up knowing where you stand",
    whoItsFor: "Individuals seeking ongoing guidance and life clarity",
    depthLevel: "Understand WHY",
    emoji: "✨",
    iconName: "Sparkles",
    color: "var(--burning-flame)",
    bgGradient: "from-[rgba(200,100,50,0.08)] to-[var(--bg-surface)]",
    borderColor: "border-[var(--burning-flame)]",
    textColor: "text-[var(--burning-flame)]",
    period: "month",
    cta: "Begin Your Path",
    ctaStyle: "primary",
    unlocks: [
      "7-day Free Trial — no card required",
      "Everything at deeper level",
      "Full Daily Horoscope with reasoning",
      "Detailed Birth Chart with yogas",
      "Timing with Confidence Scores",
      "AI with detailed explanations",
      "8 Premium Reports (full)",
      "Personalized Remedies",
    ],
    features: [
      { name: "Everything in PRARAMBH at deeper level", included: true, highlight: true },
      { name: "7-day free trial — no card required", included: true, special: true },
      { name: "Full Daily Horoscope with reasoning", included: true },
      { name: "Weekly & Monthly Forecasts", included: true },
      { name: "Complete Birth Chart with yogas", included: true },
      { name: "Dasha Predictions with timing", included: true },
      { name: "Compatibility Analysis (multi-factor)", included: true },
      { name: "Confidence Scores", included: true },
      { name: "Personalized Remedies", included: true },
      { name: "Unlimited AI (detailed answers)", included: true },
      { name: "8 Premium Reports (full)", included: true },
      { name: "PDF Downloads", included: true },
    ],
    excludedHighlights: [
      "Kids Gurukul (Audio + Hindi)",
      "Family Features (KULA only)",
      "Up to 6 Family Members",
      "Priority AI Responses",
      "Advanced Vedic Analysis",
    ],
  },
  kula: {
    id: "kula",
    nameEnglish: "The Family",
    tagline: "A complete system for family decisions and life direction",
    description: "Family wisdom system — Move through life together",
    whoItsFor: "Families and serious practitioners seeking complete guidance",
    depthLevel: "Know WHY + WHEN + WHAT TO DO",
    emoji: "🔱",
    iconName: "Users",
    color: "var(--truffle)",
    bgGradient: "from-[var(--truffle-light)] to-[var(--bg-surface)]",
    borderColor: "border-[var(--truffle)]",
    textColor: "text-[var(--truffle)]",
    period: "month",
    cta: "Access Full Wisdom",
    ctaStyle: "secondary",
    unlocks: [
      "Up to 6 Family Members",
      "ALL 12 Premium Reports FREE",
      "Everything at expert level",
      "Kids Gurukul (304 Stories + Audio + Hindi)",
      "Family Synthesis",
      "Multi-System Validation",
      "Action Pathways & Risk Scoring",
    ],
    features: [
      { name: "Everything in MARGA at expert level", included: true, highlight: true },
      { name: "ALL 12 Premium Reports FREE", included: true, special: true },
      { name: "Up to 6 Family Members", included: true, special: true },
      { name: "Kids Gurukul (304 Stories + Audio + Hindi)", included: true, special: true },
      { name: "Family Dashboard", included: true },
      { name: "Family Synthesis", included: true },
      { name: "Unlimited AI (comprehensive answers)", included: true },
      { name: "Priority AI Responses", included: true },
      { name: "Multi-System Validation (all dashas)", included: true },
      { name: "Divisional Charts & Kalachakra", included: true },
      { name: "Action Pathways", included: true },
      { name: "Risk Scoring", included: true },
    ],
    uniqueValueProps: [
      "Up to 6 family members per subscription",
      "Only platform with 304 Hindu stories for kids",
      "Only platform with family synthesis",
      "Only platform with family-first architecture",
    ],
    excludedHighlights: [],
  },
};


// ============================================================================
// ASK AADHYAA QUESTION PACKS
// ============================================================================

export const ASK_PACKS = {
  ask_5: {
    id: "ask_5",
    name: "5 Questions Pack",
    questions: 5,
    priceINR: 699,
    priceUSD: 8.99,
    description: "5 in-depth AI conversations",
  },
  ask_10: {
    id: "ask_10",
    name: "10 Questions Pack",
    questions: 10,
    priceINR: 999,
    priceUSD: 12.99,
    description: "10 in-depth AI conversations",
    isPopular: true,
  },
  ask_25: {
    id: "ask_25",
    name: "25 Questions Pack",
    questions: 25,
    priceINR: 1999,
    priceUSD: 24.99,
    description: "25 in-depth AI conversations",
  },
};


// ============================================================================
// SIMULATION ENGINE PRICING
// ============================================================================

export const SIMULATION_PRICING = {
  deep_simulation: {
    id: "deep_simulation",
    name: "Deep Simulation",
    description: "Run a comprehensive life scenario simulation",
    priceINR: 149,                    // REPRICED Feb 25, 2026 (PM audit Rec #11)
    priceUSD: 2.49,
    freePerMonthMarga: 1,
    freePerMonthKula: 3,
  },
};


// ============================================================================
// REPORT PRICING — STANDARDIZED
// ============================================================================

export const STANDARD_REPORT_PRICE_INR = 2499;
export const STANDARD_REPORT_PRICE_USD = 69;
export const ADVANCED_REPORT_PRICE_INR = 2999;
export const ADVANCED_REPORT_PRICE_USD = 79;

export const REPORT_PRICING = {
  "life-phase-evolution": {
    id: "life-phase-evolution",
    title: "Life Phase & Evolution Report",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    cluster: "decisions-timing",
    minTier: "marga",
    freeForKula: true,
  },
  "decision-confidence": {
    id: "decision-confidence",
    title: "Decision Confidence Deep Report",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    cluster: "decisions-timing",
    minTier: "marga",
    freeForKula: true,
  },
  "career-timing": {
    id: "career-timing",
    title: "Career Timing & Decision Report",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    cluster: "decisions-timing",
    minTier: "marga",
    freeForKula: true,
  },
  "event-muhurta": {
    id: "event-muhurta",
    title: "Event Timing & Muhurta Action Report",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    cluster: "decisions-timing",
    minTier: "marga",
    freeForKula: true,
  },
  "marriage-compatibility": {
    id: "marriage-compatibility",
    title: "Marriage & Compatibility Intelligence Report",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    cluster: "relationships-family",
    minTier: "marga",
    freeForKula: true,
  },
  "family-intelligence": {
    id: "family-intelligence",
    title: "Family Intelligence Report",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    cluster: "relationships-family",
    minTier: "kula",
    freeForKula: true,
  },
  "child-development": {
    id: "child-development",
    title: "Child Emotional & Developmental Intelligence Report",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    cluster: "relationships-family",
    minTier: "kula",
    freeForKula: true,
  },
  "annual-planner": {
    id: "annual-planner",
    title: "Annual Strategic Planner",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    cluster: "growth-planning",
    minTier: "marga",
    freeForKula: true,
  },
  "health-energy": {
    id: "health-energy",
    title: "Health & Energy Correlation Report",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    cluster: "growth-planning",
    minTier: "marga",
    freeForKula: true,
  },
  "business-entrepreneurship": {
    id: "business-entrepreneurship",
    title: "Business & Entrepreneurship Timing Report",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    cluster: "advanced-professional",
    minTier: "marga",
    freeForKula: true,
  },
  "team-compatibility": {
    id: "team-compatibility",
    title: "Group / Team Compatibility & Dynamics Report",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    cluster: "advanced-professional",
    minTier: "marga",
    freeForKula: true,
  },
  "advanced-vedic": {
    id: "advanced-vedic",
    title: "Advanced Multi-System Report",
    priceINR: ADVANCED_REPORT_PRICE_INR,
    priceUSD: ADVANCED_REPORT_PRICE_USD,
    cluster: "advanced-professional",
    minTier: "kula",
    freeForKula: true,
    isAdvanced: true,
  },
};


// ============================================================================
// STANDALONE INSIGHTS (sold as one-time purchases at premium-report pricing)
// ============================================================================

export const STANDALONE_INSIGHTS = [
  {
    id: "career_focus_insight",
    name: "Career Focus Insight",
    subtitle: "Current Phase Guidance",
    description: "Focused clarity on your career path right now",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    includedIn: "MARGA",
    positioningCopy: "This focused insight may support you if you're seeking clarity on your career right now.",
    paymentNote: "Full payment required before report generation",
  },
  {
    id: "relationship_focus_insight",
    name: "Relationship Focus Insight",
    subtitle: "Present Context Analysis",
    description: "Understand your relationship dynamics in the present moment",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    includedIn: "MARGA",
    positioningCopy: "This focused insight may help if you're navigating relationship questions right now.",
    paymentNote: "Full payment required before report generation",
  },
  {
    id: "year_ahead_overview",
    name: "Year-Ahead Overview",
    subtitle: "High-Level Annual Guidance",
    description: "A bird's eye view of your coming year",
    priceINR: STANDARD_REPORT_PRICE_INR,
    priceUSD: STANDARD_REPORT_PRICE_USD,
    includedIn: "MARGA",
    positioningCopy: "This overview may serve you if you're planning a significant year ahead.",
    paymentNote: "Full payment required before report generation",
  },
  {
    id: "child_values_snapshot",
    name: "Child Values Snapshot",
    subtitle: "Understanding Your Child's Path",
    description: "Discover your child's natural learning style and values",
    priceINR: 699,
    priceUSD: 8.99,
    includedIn: "KULA",
    positioningCopy: "This snapshot may guide you in supporting your child's unique path.",
    paymentNote: "Full payment required before report generation",
  },
];


// ============================================================================
// 3-TIER COMPARISON TABLE (Pricing page comparison widget)
// ============================================================================

export const TIER_COMPARISON = [
  {
    category: "Daily Guidance",
    features: [
      { name: "Daily Snapshot", prarambh: "Basic", marga: "Full", kula: "Full" },
      { name: "Weekly Forecast", prarambh: false, marga: true, kula: true },
      { name: "Monthly Overview", prarambh: false, marga: true, kula: true },
    ],
  },
  {
    category: "Birth Chart",
    features: [
      { name: "Basic (Rashi, Nakshatra)", prarambh: true, marga: true, kula: true },
      { name: "Complete (All Houses)", prarambh: false, marga: true, kula: true },
      { name: "Dasha Predictions", prarambh: false, marga: true, kula: true },
    ],
  },
  {
    category: "AI Conversations",
    features: [
      { name: "Limit", prarambh: "3/week (brief)", marga: "Unlimited (detailed)", kula: "Unlimited (expert)" },
      { name: "Priority Responses", prarambh: false, marga: false, kula: true },
    ],
  },
  {
    category: "Family Features",
    features: [
      { name: "Family Members (total)", prarambh: "1", marga: "3", kula: "6" },
      { name: "Kids Gurukul", prarambh: false, marga: false, kula: "304 Stories" },
      { name: "Family Synthesis", prarambh: false, marga: false, kula: true },
    ],
  },
  {
    category: "Reports & Export",
    features: [
      { name: "Premium Reports", prarambh: "0", marga: "8", kula: "12 (all)" },
      { name: "PDF Download", prarambh: false, marga: true, kula: true },
    ],
  },
];


// ============================================================================
// FAQ ITEMS (Pricing page + FAQPage JSON-LD for rich snippets)
// ============================================================================

export const FAQ_ITEMS = [
  {
    question: "Why does AADHYAA cost what it costs?",
    answer: "AADHYAA combines authentic Vedic wisdom with responsible AI to deliver personalized guidance that respects your intelligence. Our pricing reflects the depth of research, the quality of interpretations, and our commitment to being a calm, trustworthy companion — not a sensationalized prediction service.",
  },
  {
    question: "What's the difference between PRARAMBH, MARGA, and KULA?",
    answer: "PRARAMBH (Free) lets you experience AADHYAA with all sections visible at surface depth — 3 deep AI questions/week + unlimited brief replies. MARGA (₹899/mo) is for individuals seeking complete daily guidance with full horoscopes, Dasha predictions, and detailed explanations of WHY things happen — and starts with a 7-day free trial. KULA (₹1,999/mo) is designed for families — Kids Gurukul with 304 stories (audio + Hindi), up to 6 family members, ALL 12 Premium Reports FREE (₹29,988 of standalone value), and complete guidance showing WHY + WHEN + WHAT TO DO.",
  },
  {
    question: "Is KULA worth it if I don't have children?",
    answer: "KULA is optimized for families, but even without children, you get unlimited AI conversations with comprehensive answers, priority responses, ALL 12 reports completely FREE (worth ₹29,988), multi-system dasha validation, action pathways, and advanced Vedic features like Kalachakra. If you're an individual without family needs, MARGA provides excellent value.",
  },
  {
    question: "What's the difference between monthly and yearly billing?",
    answer: "Monthly billing gives you flexibility to cancel anytime. Yearly billing saves you 20% — like getting 2.4 months free. Both give you full access to your tier's depth from day one.",
  },
  {
    question: "Can I upgrade later?",
    answer: "Absolutely. You can upgrade from PRARAMBH to MARGA, or from MARGA to KULA at any time. Your journey with us should feel natural, not pressured.",
  },
  {
    question: "What about standalone insights?",
    answer: "Standalone insights are for specific, one-time situations where you need focused clarity. They're priced above the monthly subscription because subscriptions offer ongoing value. If you find yourself needing multiple insights, MARGA offers better long-term value.",
  },
];


// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get a tier with pricing + visual metadata merged.
 * Use this in components instead of redefining tier data.
 */
export const getTier = (id) => ({
  ...TIER_META[id],
  ...TIER_PRICING[id],
});

/**
 * Get all 3 tiers as an ordered array (PRARAMBH → MARGA → KULA).
 */
export const getTiers = () => ["prarambh", "marga", "kula"].map(getTier);

/**
 * Format price with currency symbol.
 */
export const formatPrice = (amount, isIndia = true) => {
  if (amount === 0) return "Free";
  if (isIndia) {
    return `₹${amount.toLocaleString("en-IN")}`;
  }
  return `$${amount.toFixed(2)}`;
};

/**
 * Get currency symbol.
 */
export const getCurrencySymbol = (isIndia = true) => (isIndia ? "₹" : "$");

/**
 * Get tier price for a given billing cycle and currency.
 */
export const getTierPrice = (tierId, billingCycle = "monthly", isIndia = true) => {
  const tier = TIER_PRICING[tierId];
  if (!tier) return 0;
  if (billingCycle === "yearly") {
    return isIndia ? tier.yearlyPriceINR : tier.yearlyPriceUSD;
  }
  return isIndia ? tier.monthlyPriceINR : tier.monthlyPriceUSD;
};

/**
 * Get report price (returns 0 for KULA users).
 */
export const getReportPrice = (reportId, userTier = "prarambh", isIndia = true) => {
  if (userTier.toLowerCase() === "kula") return 0;
  const report = REPORT_PRICING[reportId];
  if (!report) {
    return isIndia ? STANDARD_REPORT_PRICE_INR : STANDARD_REPORT_PRICE_USD;
  }
  return isIndia ? report.priceINR : report.priceUSD;
};

/**
 * Get Ask pack price.
 */
export const getAskPackPrice = (packId, isIndia = true) => {
  const pack = ASK_PACKS[packId];
  if (!pack) return 0;
  return isIndia ? pack.priceINR : pack.priceUSD;
};

/**
 * Get simulation price.
 */
export const getSimulationPrice = (isIndia = true) => {
  const sim = SIMULATION_PRICING.deep_simulation;
  return isIndia ? sim.priceINR : sim.priceUSD;
};

/**
 * Calculate annual savings details for a tier.
 */
export const getAnnualSavings = (tierId, isIndia = true) => {
  const tier = TIER_PRICING[tierId];
  if (!tier || tier.isFree) {
    return { savingsAmount: 0, savingsPercent: 0, freeMonths: 0 };
  }
  const monthly = isIndia ? tier.monthlyPriceINR : tier.monthlyPriceUSD;
  const yearly = isIndia ? tier.yearlyPriceINR : tier.yearlyPriceUSD;
  const fullYearPrice = monthly * 12;
  const savings = fullYearPrice - yearly;
  const savingsPercent = Math.round((savings / fullYearPrice) * 100);
  return {
    savingsAmount: savings,
    savingsPercent,
    monthlyEquivalent: Math.round((yearly / 12) * 100) / 100,
  };
};

/**
 * Check if user has access to a feature based on tier.
 */
export const hasFeatureAccess = (userTier, requiredTier) => {
  const tierOrder = {
    prarambh: 1,
    marga: 2,
    kula: 3,
    premium: 3,
    tattva: 3,
    family: 3,
  };
  return (tierOrder[userTier.toLowerCase()] || 1) >= (tierOrder[requiredTier.toLowerCase()] || 1);
};


// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  TIER_PRICING,
  TIER_META,
  ASK_PACKS,
  SIMULATION_PRICING,
  REPORT_PRICING,
  STANDALONE_INSIGHTS,
  TIER_COMPARISON,
  FAQ_ITEMS,
  STANDARD_REPORT_PRICE_INR,
  STANDARD_REPORT_PRICE_USD,
  ADVANCED_REPORT_PRICE_INR,
  ADVANCED_REPORT_PRICE_USD,
  getTier,
  getTiers,
  formatPrice,
  getCurrencySymbol,
  getTierPrice,
  getReportPrice,
  getAskPackPrice,
  getSimulationPrice,
  getAnnualSavings,
  hasFeatureAccess,
};
